import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Box, Button, Flex, IconButton, Text, Tooltip, useToast } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import {
  useExportTotalEmissions,
  useGetSuppliersEmissionsReports,
  useGetSuppliersYearlyData,
} from "../../../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import {
  calculateDataTableState,
  constructQueryParamsString,
  getFormattedNumValue,
  getSortDirection,
  handlePageSizeChange,
  showErrorToast,
  translateSubElementString,
} from "../../../../utils";
import { DataTable } from "../../../../components/DataTable";
import * as React from "react";
import { MultiSelect, YearSelector } from "../../../../components";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../../config";
import { FaExclamationCircle } from "react-icons/fa";
import { getFilteredData } from "../../utils";
import EmissionFilterComponent from "../EmissionFilterComponent";

function TotalEmissionsTab({ selectedYear, setSelectedYear }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "suppliersEmissionsDashboard",
  });
  const dispatch = useDispatch();
  const toast = useToast();
  const navigate = useNavigate();
  const [yearByYearEmissionsData, setYearByYearEmissionsData] = useState([]);
  const [years, setYears] = useState([selectedYear]);
  const [selectedDataQualityFilters, setSelectedDataQualityFilters] = useState([]);

  useEffect(() => {
    setYears([selectedYear]);
  }, [selectedYear]);

  const columns = useMemo(() => {
    function handleSupplierClick(el) {
      console.log(el.row?.original?.supplierId);
      navigate(`${routes.supplierEmissionsReport}?id=${el?.row?.original?.supplierId ?? ""}&year=${selectedYear}`);
    }

    return [
      {
        header: t("totalEmissions.gridHeaders.supplier"),
        accessorKey: "supplierName",
        size: 130,
        cell: (el) => {
          return (
            <Text textDecoration={"underline"} onClick={() => handleSupplierClick(el)} cursor="pointer">
              {el.row?.original?.supplierName ?? ""}
            </Text>
          );
        },
        customStyles: {
          verticalAlign: "top",
        },
      },
      {
        header: translateSubElementString("suppliersEmissionsDashboard.totalEmissions.gridHeaders.scope1Emissions"),
        accessorKey: "scope1TotalCo2Emissions",
        size: 100,
        customStyles: {
          verticalAlign: "top",
        },
        enableColumnFilter: false,
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.yearlyEmissionData?.[selectedYear]?.scope1 || 0;
          const b = rowB.original.yearlyEmissionData?.[selectedYear]?.scope1 || 0;
          return a - b;
        },
        cell: ({ row }) => getFormattedNumValue(row.original?.yearlyEmissionData?.[selectedYear]?.scope1 || 0, 2),
      },
      {
        header: translateSubElementString("suppliersEmissionsDashboard.totalEmissions.gridHeaders.scope2Emissions"),
        accessorKey: "scope2TotalCo2Emissions",
        size: 100,
        customStyles: {
          verticalAlign: "top",
        },
        enableColumnFilter: false,
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.yearlyEmissionData?.[selectedYear]?.scope2 || 0;
          const b = rowB.original.yearlyEmissionData?.[selectedYear]?.scope2 || 0;
          return a - b;
        },
        cell: ({ row }) => getFormattedNumValue(row.original?.yearlyEmissionData?.[selectedYear]?.scope2 || 0, 2),
      },
      {
        header: (
          <Flex alignItems="center">
            {translateSubElementString("suppliersEmissionsDashboard.totalEmissions.gridHeaders.scope3Emissions")}
            <Tooltip label={t("totalEmissions.scope3EmissionsRange")} whiteSpace={"pre-line"} hasArrow>
              <IconButton
                aria-label="scope-three-emissions-range-info"
                type="button"
                icon={<FaExclamationCircle size="16" />}
                flexShrink="0"
                size="2xs"
                opacity="0.25"
                tabIndex={-1}
                marginInlineStart={2}
              />
            </Tooltip>
          </Flex>
        ),
        accessorKey: "scope3TotalCo2Emissions",
        size: 110,
        enableColumnFilter: false,
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.yearlyEmissionData?.[selectedYear]?.scope3 || 0;
          const b = rowB.original.yearlyEmissionData?.[selectedYear]?.scope3 || 0;
          return a - b;
        },
        cell: ({ row }) => getFormattedNumValue(row.original?.yearlyEmissionData?.[selectedYear]?.scope3 || 0, 2),
      },
      {
        header: translateSubElementString("suppliersEmissionsDashboard.totalEmissions.gridHeaders.overallEmissions"),
        accessorKey: "totalCo2Emissions",
        size: 100,
        customStyles: {
          verticalAlign: "top",
        },
        enableColumnFilter: false,
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.yearlyEmissionData?.[selectedYear]?.totalCo2Emissions || 0;
          const b = rowB.original.yearlyEmissionData?.[selectedYear]?.totalCo2Emissions || 0;
          return a - b;
        },
        cell: ({ row }) => {
          const value = row.original?.yearlyEmissionData?.[selectedYear]?.totalCo2Emissions || 0;
          return <Text color={value > 100000 ? "red" : "black"}>{getFormattedNumValue(value, 2)}</Text>;
        },
      },
    ];
  }, [t, navigate, selectedYear]);

  const getQueryParamsString = () => {
    const queryParams = {
      years: years,
    };
    return constructQueryParamsString(queryParams);
  };

  const { isFetching } = useGetSuppliersYearlyData(getQueryParamsString(), {
    onSuccess: (data) => {
      setYearByYearEmissionsData(data);
      // handlePageSizeChange(page, queryPageIndex, manageDataTableState);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
  });

  const filteredData = useMemo(() => {
    return getFilteredData(yearByYearEmissionsData, selectedDataQualityFilters);
  }, [yearByYearEmissionsData, selectedDataQualityFilters]);

  const handleErrors = (error) => {
    let errorMessage = error.response?.data?.parameters?.message ?? error.message;
    dispatch(hideAppSpinner());
    showErrorToast(toast, errorMessage);
  };

  const { mutate: exportTotalEmissions } = useExportTotalEmissions({
    onMutate: () => dispatch(showAppSpinner()),
    onError: (err) => handleErrors(err),
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const handleExport = () => {
    exportTotalEmissions({ year: selectedYear });
  };

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Flex direction="column" gap={4} mt={8}>
          <YearSelector selectedYear={selectedYear} handleYearSelection={(value) => setSelectedYear(value)} />
          <EmissionFilterComponent
            type="dataQuality"
            selectedValues={selectedDataQualityFilters}
            onChange={(e) => setSelectedDataQualityFilters(e)}
          />
        </Flex>
        <Button colorScheme="customScheme" onClick={handleExport}>
          {t("exportData")}
        </Button>
      </Flex>
      <DataTable
        data={filteredData}
        columns={columns}
        isLoading={isFetching}
        enableSorting={true}
        enableFilters={true}
        showDownloadButton={false}
      />
    </Box>
  );
}

export default TotalEmissionsTab;
